import { useState, useEffect, useCallback, useRef } from 'react';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';
import { useAuth } from './useAuth';
import { useOrganizationFeatures } from './useOrganizationFeatures';
import { CacheService } from '@/services';

import { logger } from '@/utils/productionLogger';

interface Referent {
  id: string;
  refused_at: string | null;
  status: string;
}

interface Campaign {
  id: string;
  candidate_name: string;
  candidate_email: string;
  target_position: string;
  status: string;
  is_draft: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  current_step: number;
  recruiter_id: string;
  ai_extracted_data?: any;
  referents_requested_count?: number;
  referents?: Referent[];
}

interface UseCampaignsWithPollingOptions {
  enabled?: boolean;
  includeCompleted?: boolean;
  pollingInterval?: number; // en millisecondes
}

/**
 * Hook pour gérer les campagnes avec mise à jour automatique par polling
 * Plus simple et fiable que le temps réel WebSocket
 * Adapté au nouveau modèle d'accès par rôle (member/admin/owner)
 */
export const useCampaignsWithPolling = ({
  enabled = true,
  includeCompleted = false,
  pollingInterval = 5000 // 5 secondes par défaut - Quasi temps réel
}: UseCampaignsWithPollingOptions = {}) => {
  const { user, profile } = useAuth();
  const { organizationData } = useOrganizationFeatures();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);

  // Refs pour éviter les boucles d'effet
  const campaignsRef = useRef<Campaign[]>([]);
  const inFlightRef = useRef(false);
  
  const userRole = organizationData?.userRole;
  const orgId = profile?.organization_id;

  // Mettre à jour la ref quand campaigns change
  useEffect(() => {
    campaignsRef.current = campaigns;
  }, [campaigns]);

  // Fonction pour charger les campagnes
  const loadCampaigns = useCallback(async (useCache = true): Promise<Campaign[] | undefined> => {
    if (!user?.id || !enabled) {
      setCampaigns([]);
      setLoading(false);
      return [];
    }

    if (!orgId) {
      logger.warn('⚠️ User not associated with an organization');
      setCampaigns([]);
      setLoading(false);
      return [];
    }

    // Éviter les appels concurrents
    if (inFlightRef.current) {
      logger.debug('🔄 Chargement déjà en cours, skip');
      return campaignsRef.current;
    }

    try {
      inFlightRef.current = true;
      setError(null);

      // 1. Essayer le cache d'abord (si demandé)
      // Cache key inclut le rôle pour éviter les collisions
      const cacheKey = CacheService.generateKey('campaigns', user.id, userRole || 'unknown');
      
      if (useCache) {
        const cached = CacheService.get<Campaign[]>(cacheKey);
        
        if (cached) {
          // Comparer avec les données actuelles pour éviter un setState inutile
          const currentIds = campaignsRef.current.map(c => `${c.id}-${c.updated_at}`).sort().join(',');
          const cachedIds = cached.map(c => `${c.id}-${c.updated_at}`).sort().join(',');
          
          if (currentIds !== cachedIds) {
            setCampaigns(cached);
            logger.debug('✅ Campagnes mises à jour depuis le cache', { count: cached.length });
          } else {
            logger.debug('✅ Campagnes identiques en cache, pas de setState');
          }
          
          setLoading(false);
          return cached;
        }
      }

      // 2. Charger depuis la base de données avec gestion d'authentification
      setLoading(true);
      
      // Utiliser executeWithRetry pour intercepter les erreurs d'authentification
      const { data, error: queryError } = await supabaseWithAuth.executeWithRetry(async () => {
        let query = supabaseWithAuth
          .from('campaigns')
          .select(`
            *,
            referents (
              id,
              refused_at,
              status
            )
          `)
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false });

        // Filtre selon le rôle (aligné avec useCampaignsQuery)
        // member: voit uniquement ses campagnes
        // admin/owner: voient toutes les campagnes de l'org (RLS gère déjà)
        if (userRole === 'member') {
          logger.debug('🔍 [useCampaignsWithPolling] Applying member filter');
          query = query.eq('recruiter_id', user.id);
        } else {
          logger.debug('🔍 [useCampaignsWithPolling] Admin/Owner - no additional filter');
        }

        // Filtrer les campagnes terminées si nécessaire
        if (!includeCompleted) {
          query = query.neq('status', 'Terminé');
        }

        return query;
      }, 'campaigns.load');

      if (queryError) {
        throw queryError;
      }

      const campaignsData = (data || []) as unknown as Campaign[];
      
      logger.debug('🔍 [useCampaignsWithPolling] Query result:', {
        dataCount: campaignsData.length,
        userRole,
        orgId
      });
      
      // 3. Comparer avec les données actuelles avant setState
      const currentIds = campaignsRef.current.map(c => `${c.id}-${c.updated_at}`).sort().join(',');
      const newIds = campaignsData.map(c => `${c.id}-${c.updated_at}`).sort().join(',');
      
      if (currentIds !== newIds) {
        // 4. Mettre en cache (TTL de 2 minutes pour polling plus réactif)
        CacheService.set(cacheKey, campaignsData, 2 * 60 * 1000);

        // 5. Mettre à jour l'état seulement si différent
        setCampaigns(campaignsData);
        setLastFetch(Date.now());
        
        logger.debug('✅ Campagnes mises à jour depuis la DB', { 
          count: campaignsData.length,
          fromCache: false 
        });
      } else {
        logger.debug('✅ Campagnes identiques en DB, pas de setState');
      }

      return campaignsData;

    } catch (err: any) {
      logger.error('❌ Erreur chargement campagnes', err as Error, {
        context: 'useCampaignsWithPolling',
        userId: user.id,
        orgId,
        userRole,
        includeCompleted,
        useCache
      });
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
      inFlightRef.current = false;
      logger.debug('🔄 END loadCampaigns');
    }
  }, [user?.id, orgId, userRole, enabled, includeCompleted]);

  // Chargement initial
  useEffect(() => {
    // Attendre que toutes les dépendances critiques soient prêtes
    if (user?.id && orgId && userRole) {
      loadCampaigns(true);
    }
  }, [loadCampaigns, user?.id, orgId, userRole]);

  // Polling automatique intelligent
  useEffect(() => {
    if (!enabled || !user?.id) return;

    logger.debug('🧪 polling effect mounted');

    const getAdaptiveInterval = () => {
      // Plus rapide si l'onglet est actif
      const isTabActive = typeof document !== 'undefined' ? !document.hidden : true;
      return isTabActive ? pollingInterval : pollingInterval * 3; // 3x plus lent si inactif
    };

    let timeoutId: NodeJS.Timeout;

    const scheduleNextPoll = () => {
      const interval = getAdaptiveInterval();
      
      timeoutId = setTimeout(async () => {
        logger.debug('🔄 Polling automatique des campagnes', { interval });
        
        // Charger sans cache pour avoir les données fraîches
        await loadCampaigns(false);
        
        // Programmer le prochain polling
        scheduleNextPoll();
      }, interval);
    };

    // Démarrer le polling
    scheduleNextPoll();

    // Écouter les changements de visibilité pour adapter l'intervalle
    const handleVisibilityChange = () => {
      if (timeoutId) clearTimeout(timeoutId);
      scheduleNextPoll(); // Redémarrer avec le bon intervalle (pas de load immédiat)
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      logger.debug('🧪 polling effect cleanup');
    };
  }, [enabled, user?.id, pollingInterval, loadCampaigns]); // ✅ campaigns supprimé des deps

  // Fonction pour forcer le refresh
  const refresh = useCallback(() => {
    logger.debug('🔄 Refresh manuel des campagnes');
    // Invalider le cache et recharger
    const cacheKey = CacheService.generateKey('campaigns', user?.id || '', userRole || 'unknown');
    CacheService.invalidate(cacheKey);
    return loadCampaigns(false); // Forcer le rechargement sans cache
  }, [loadCampaigns, user?.id, userRole]);

  // Fonction pour obtenir une campagne spécifique
  const getCampaign = useCallback((id: string) => {
    return campaigns.find(campaign => campaign.id === id);
  }, [campaigns]);

  // Statistiques
  const stats = {
    total: campaigns.length,
    drafts: campaigns.filter(c => c.is_draft).length,
    active: campaigns.filter(c => !c.is_draft && c.status !== 'Terminé').length,
    completed: campaigns.filter(c => c.status === 'Terminé').length
  };

  return {
    campaigns,
    loading,
    error,
    stats,
    refresh,
    getCampaign,
    lastFetch,
    isConnected: true, // Toujours connecté (pas de WebSocket à gérer)
  };
};